import { useId, useMemo, useState, type KeyboardEvent } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Empty,
  EmptyDescription,
  EmptyTitle,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { ArrowDownWideNarrowIcon, ArrowUpNarrowWideIcon } from 'lucide-react';
import { Link } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';

import type { PersonaDto } from '@/lib/api/personas-client';
import { ROUTES } from '@/shared/config/routes';
import { SearchInput } from '@/shared/ui/SearchInput';
import { formatShortDate } from '@/shared/utils/format';
import { useAnalysisFormStore } from '../../../model/analysisFormStore';

const MAX_TAGS_DISPLAYED = 3;

type SortOrder = 'newest' | 'oldest';

const audienceName = (persona: PersonaDto): string =>
  persona.segmentName || persona.personaTitle || '';

/** Age first, then interests: the age range is what every audience has to compare on. */
const audienceTags = (persona: PersonaDto): string[] => {
  const { age, interests = [] } = persona.demographicsData?.data ?? {};
  return [age, ...interests].filter((tag): tag is string => Boolean(tag));
};

const matchesSearch = (persona: PersonaDto, query: string): boolean =>
  [audienceName(persona), persona.segmentDescription ?? '', ...audienceTags(persona)].some(
    (value) => value.toLowerCase().includes(query),
  );

interface AudienceOptionProps {
  persona: PersonaDto;
  selected: boolean;
  onToggle: () => void;
}

/**
 * The card is the checkbox - `role` and `aria-checked` sit on it and the design system's
 * Checkbox is left as its picture, so the whole card is one control rather than a button with
 * a second control inside it. Same reason the mode cards above carry `role="radio"`.
 */
const AudienceOption = ({ persona, selected, onToggle }: AudienceOptionProps) => {
  const { t } = useTranslation('analysis');

  // A checkbox takes its name from the author rather than its contents, so the card points at
  // the audience name for one - otherwise it announces as an unnamed checkbox.
  const nameId = useId();
  const name = audienceName(persona) || t('analysis.knowledgeHubAudiencePicker.unknownSegment');
  const tags = audienceTags(persona).slice(0, MAX_TAGS_DISPLAYED);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    // A held key repeats, and a native checkbox activates once - so a long press must not
    // toggle the card over and over and land wherever the release happened to fall.
    if (event.repeat) return;
    event.preventDefault();
    onToggle();
  };

  return (
    <Item
      asChild
      variant="outline"
      size="sm"
      // The checkbox is the only thing that marks a pick, so every card keeps one surface
      // and one border however many are chosen.
      className="cursor-pointer items-start gap-sm rounded-xl bg-background border-border-light hover:border-primary"
    >
      <div
        role="checkbox"
        aria-checked={selected}
        aria-labelledby={nameId}
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
      >
        <ItemMedia>
          <Checkbox checked={selected} aria-hidden tabIndex={-1} className="pointer-events-none" />
        </ItemMedia>

        <ItemContent className="gap-2xs">
          <ItemTitle id={nameId} className="type-body-md-strong text-foreground">
            {name}
          </ItemTitle>

          {persona.segmentDescription && (
            // An audience is chosen on what its description says, so it is read in full
            // rather than clamped to the primitive's two lines.
            <ItemDescription className="type-body-md line-clamp-none text-foreground-subtle">
              {persona.segmentDescription}
            </ItemDescription>
          )}

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2xs">
              {tags.map((tag, index) => (
                <Badge key={`${tag}-${index}`} variant="secondary" size="sm" roundness="full">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </ItemContent>

        <ItemActions className="type-body-sm text-foreground-subtle">
          {t('analysis.knowledgeHubAudiencePicker.added', {
            date: formatShortDate(persona.created),
          })}
        </ItemActions>
      </div>
    </Item>
  );
};

interface KnowledgeHubAudiencePickerProps {
  /**
   * Fetched at the page boundary (`pages/NewAnalysis`) and passed in, so `features/analysis`
   * never imports another feature slice - which FSD forbids and its boundary test enforces.
   */
  personas: PersonaDto[];
  isPending: boolean;
}

/**
 * Hand-picking audiences the workspace already has.
 *
 * The prototype also filters by who created an audience; the persona API carries no author,
 * only `created`, so the card states the date alone and the filter is left out until it does.
 */
export const KnowledgeHubAudiencePicker = ({
  personas,
  isPending,
}: KnowledgeHubAudiencePickerProps) => {
  const { t } = useTranslation('analysis');

  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');

  const selectedPersonaIds = useAnalysisFormStore((s) => s.selectedPersonaIds);
  const addSelectedPersonaId = useAnalysisFormStore((s) => s.addSelectedPersonaId);
  const removeSelectedPersonaId = useAnalysisFormStore((s) => s.removeSelectedPersonaId);

  const visiblePersonas = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matched = query ? personas.filter((p) => matchesSearch(p, query)) : personas;

    return [...matched].sort((a, b) => {
      const gap = new Date(b.created).getTime() - new Date(a.created).getTime();
      return sortOrder === 'newest' ? gap : -gap;
    });
  }, [personas, search, sortOrder]);

  if (isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (personas.length === 0) {
    return (
      <Empty variant="outline">
        <EmptyTitle>{t('analysis.knowledgeHubAudiencePicker.noAudiencesYet')}</EmptyTitle>
        <EmptyDescription>
          <Trans
            i18nKey="analysis.knowledgeHubAudiencePicker.addOneInTheKnowledgeHub"
            ns="analysis"
            components={{ hubLink: <Link to={ROUTES.KNOWLEDGE_HUB_AUDIENCES} /> }}
          />
        </EmptyDescription>
      </Empty>
    );
  }

  const SortIcon = sortOrder === 'newest' ? ArrowDownWideNarrowIcon : ArrowUpNarrowWideIcon;

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-wrap items-center gap-xs mt-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('analysis.knowledgeHubAudiencePicker.searchByNameTraitOrTag')}
          aria-label={t('analysis.knowledgeHubAudiencePicker.searchAudiences')}
          className="w-full max-w-[350px]"
        />

        <Button
          variant="outline"
          onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
        >
          <SortIcon className="text-foreground-alt" />
          {sortOrder === 'newest'
            ? t('analysis.knowledgeHubAudiencePicker.newestFirst')
            : t('analysis.knowledgeHubAudiencePicker.oldestFirst')}
        </Button>

        {/* Counts every pick, not the ones the current search leaves on screen: the step is
            valid on the whole selection, and a filtered count would drop as you type. */}
        <span
          role="status"
          className={`ml-auto ${
            selectedPersonaIds.length > 0
              ? 'type-body-sm-strong text-foreground'
              : 'type-body-sm text-foreground-subtle'
          }`}
        >
          {t('analysis.knowledgeHubAudiencePicker.nAudiencesSelected', {
            count: selectedPersonaIds.length,
          })}
        </span>
      </div>

      {visiblePersonas.length === 0 ? (
        <Empty>
          <EmptyDescription>
            {t('analysis.knowledgeHubAudiencePicker.noAudiencesMatch')}
          </EmptyDescription>
        </Empty>
      ) : (
        <div className="flex flex-col gap-xs">
          {visiblePersonas.map((persona) => {
            const selected = selectedPersonaIds.includes(persona.id);
            return (
              <AudienceOption
                key={persona.id}
                persona={persona}
                selected={selected}
                onToggle={() =>
                  selected ? removeSelectedPersonaId(persona.id) : addSelectedPersonaId(persona.id)
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
